/**
 * TODAY-V5 (Seth 2026-09-29) built three separate strips here — STUCK cards,
 * AUTO NOTICES, and the "Pegasus did today" line. TODAY-REDESIGN-02 (Seth
 * 2026-10-13, approved Exhibit A "The Desk") merges the first two into ONE
 * AlertBand — a single slot above the scoreboard that is simply ABSENT on a
 * clean morning — and the did-today line moves into the Desk's quiet footer.
 *
 * What lives here now:
 *   · useStuck — the worst few things right now (board conflicts, no-heat
 *     parts, after-hours callers holding, arrived parts unbooked). Same
 *     office-wide dismissal keys as the queue rows, so one ✕ clears both.
 *   · useLatePos — POs past their expected date (the system's own notice).
 *   · AlertBand — stuck items + coverage gaps + late POs as one-line rows
 *     in one band. Renders nothing when all three sources are quiet.
 *   · useAutoToday — what ran itself (for the footer line in TodayView).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, type LucideIcon, Flame, Moon, Package, UserX } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { displayCustomerName, titleCaseIfShouty } from "@/lib/format";
import { showOnBoard } from "@/lib/boardChannel";
import { usePendingTickets } from "@/features/board/queue";
import { useOncallGaps, isoDate, addDaysISO } from "@/features/team/oncall";
import { useBoardConflicts } from "./conflicts";
import { useDismissals } from "./dismissals";

/* ─────────────────────────── shared bits ─────────────────────────── */

const chicagoToday = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });

/** Chicago midnight as an ISO instant — "today" for timestamp columns. */
function chicagoDayStartISO(): string {
  // en-CA gives YYYY-MM-DD; Chicago is UTC-5/-6 — build a conservative UTC
  // instant (midnight Chicago ≈ 05:00–06:00Z; 06:00Z is never early).
  return `${chicagoToday()}T05:00:00.000Z`;
}

function agoShort(iso: string): string {
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  if (mins < 60 * 24) return `${(mins / 60).toFixed(mins < 600 ? 1 : 0)} h`;
  return `${Math.round(mins / 60 / 24)} d`;
}

/* ─────────────────────────── STUCK ─────────────────────────── */

interface StuckItem {
  key: string;
  rank: number; // lower = worse
  at: string; // tiebreak: older first
  /** The band row's kind chip ("Board", "No heat/cool", …). */
  tag: string;
  who: string;
  what: string;
  meta?: string;
  tone: "rose" | "amber";
  icon: LucideIcon;
  cta:
    | { kind: "board"; label: string; day: string }
    | { kind: "queue"; label: string; id: string } // selects a Desk queue tab
    | { kind: "link"; label: string; to: string };
}

/** After-hours callers still holding (needs_triage rows are deliberately NOT
 *  in usePendingTickets — ONCALL-01 keeps them off the general pile).
 *  TODAY-AFTER-HOURS-DUP (Seth 2026-10-22): only callers 1–7 days old belong in
 *  the band — fresh enough to still be waiting on us, old enough to be stuck.
 *  Older ones are the queue's stale fold (Review / Sweep); the band used to
 *  shout about exactly those. */
function useOncallWaiting() {
  return useQuery({
    queryKey: ["stuck_oncall_waiting"],
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const dayMs = 24 * 3600 * 1000;
      const { data, error } = await supabase
        .from("pegasus_dispatch")
        .select("id, customer_name, city, summary, created_at, tags")
        .eq("status", "pending_schedule")
        .is("deleted_at", null)
        .contains("tags", ["oncall", "needs_triage"])
        .gte("created_at", new Date(Date.now() - 7 * dayMs).toISOString())
        .lte("created_at", new Date(Date.now() - 1 * dayMs).toISOString())
        .order("created_at", { ascending: true })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as {
        id: string; customer_name: string | null; city: string | null;
        summary: string | null; created_at: string; tags: string[] | null;
      }[];
    },
  });
}

export function useStuck() {
  const conflicts = useBoardConflicts();
  const tickets = usePendingTickets();
  const oncall = useOncallWaiting();
  const dismissals = useDismissals();

  return useMemo(() => {
    const dismissed = dismissals.data ?? new Set<string>();
    const out: StuckItem[] = [];

    // 1 — today's board conflicts (the dispatch is WRONG right now). Same
    //     dismissal keys as the Board & Routing rows, so one ✕ clears both.
    for (const c of conflicts.data ?? []) {
      if (c.dayLabel !== "today") continue;
      if (dismissed.has(`conflict:${c.dispatch}:${c.dayISO}:${c.kind}`)) continue;
      out.push({
        key: `sk_conflict:${c.dispatch}:${c.kind}`,
        rank: 0,
        at: c.dayISO,
        tag: "Board",
        who: c.customer,
        what: c.line,
        tone: "rose",
        icon: UserX,
        cta: { kind: "board", label: "Fix on Board", day: c.dayISO },
      });
    }

    // 2 — no-heat/no-cool parts nobody has ordered.
    // 3 — arrived parts with no return visit (oldest two).
    const arrived: StuckItem[] = [];
    for (const t of tickets.data ?? []) {
      if (t.lane_key !== "order_parts") continue;
      const who = displayCustomerName(null, t.customer_name) || t.summary;
      if (
        t.part_priority === "critical" &&
        (t.part_state === "needs_ordering" || t.part_state === "backordered" || t.part_state === null)
      ) {
        if (dismissed.has(`parts_need:${t.id}`)) continue;
        out.push({
          key: `sk_crit:${t.id}`,
          rank: 1,
          at: t.created_at,
          tag: "No heat/cool",
          who,
          what: `part not ordered yet${t.part_number ? ` · #${t.part_number}` : ""}`,
          meta: titleCaseIfShouty(t.city) ?? undefined,
          tone: "rose",
          icon: Flame,
          cta: {
            kind: "link",
            label: "Order (PO)",
            to: `/purchase-orders?new=1&back=today&ticket=${encodeURIComponent(t.id)}&dispatch=${encodeURIComponent(t.origin_dispatch ?? "")}&custno=${encodeURIComponent(t.custno ?? "")}&locno=${encodeURIComponent(t.locno ?? "")}&part=${encodeURIComponent(t.part_number ?? "")}&desc=${encodeURIComponent(t.summary)}${t.part_equipment ? `&equip=${encodeURIComponent(JSON.stringify(t.part_equipment))}` : ""}`,
          },
        });
      } else if (t.part_state === "arrived") {
        if (dismissed.has(`parts:${t.id}`)) continue;
        arrived.push({
          key: `sk_arrived:${t.id}`,
          rank: 3,
          at: t.created_at,
          tag: "Part in",
          who,
          what: "part on the shelf — no return visit booked",
          meta: titleCaseIfShouty(t.city) ?? undefined,
          tone: "amber",
          icon: Package,
          cta: { kind: "queue", label: "Book It", id: "parts" },
        });
      }
    }
    arrived.sort((a, b) => a.at.localeCompare(b.at));
    out.push(...arrived.slice(0, 2));

    // 4 — after-hours callers still holding (oldest two).
    for (const r of (oncall.data ?? []).slice(0, 2)) {
      out.push({
        key: `sk_oncall:${r.id}`,
        rank: 2,
        at: r.created_at,
        tag: "After hours",
        who: displayCustomerName(null, r.customer_name) || "After-hours caller",
        what: `holding ${agoShort(r.created_at)} — nobody's on it`,
        meta: titleCaseIfShouty(r.city) ?? undefined,
        tone: "amber",
        icon: Moon,
        cta: { kind: "queue", label: "Assign", id: "oncall" },
      });
    }

    out.sort((a, b) => a.rank - b.rank || a.at.localeCompare(b.at));
    return {
      items: out.slice(0, 4),
      extra: Math.max(0, out.length - 4),
      loading: conflicts.isLoading || tickets.isLoading || oncall.isLoading,
    };
  }, [conflicts.data, conflicts.isLoading, tickets.data, tickets.isLoading, oncall.data, oncall.isLoading, dismissals.data]);
}

/* ─────────────────────── LATE POs (auto notice) ─────────────────────── */

/** POs placed with a vendor whose expected date has come and gone — the
 *  system's own bulletin. Clears itself when the PO is received/billed or
 *  the ETA gets moved (chasing the vendor and bumping the date IS the fix). */
export function useLatePos() {
  return useQuery({
    queryKey: ["auto_late_pos", chicagoToday()],
    refetchInterval: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pegasus_po")
        .select("id, po_number, vendor_code, expected_date")
        .eq("status", "ordered")
        .not("expected_date", "is", null)
        .lt("expected_date", chicagoToday())
        .order("expected_date", { ascending: true })
        .limit(25);
      if (error) throw error;
      const rows = (data ?? []) as { id: string; po_number: string | null; vendor_code: string | null; expected_date: string }[];
      const codes = [...new Set(rows.map((r) => r.vendor_code).filter(Boolean))] as string[];
      let names = new Map<string, string>();
      if (codes.length > 0) {
        const { data: vs } = await supabase.from("mirror_vendor").select("vendor_code, name").in("vendor_code", codes);
        names = new Map(((vs ?? []) as { vendor_code: string; name: string | null }[]).map((v) => [v.vendor_code, (v.name ?? v.vendor_code).trim()]));
      }
      return rows.map((r) => ({ ...r, vendor: r.vendor_code ? names.get(r.vendor_code) ?? r.vendor_code : "no vendor" }));
    },
  });
}

/* ─────────────────────────── THE ALERT BAND ─────────────────────────── */

/**
 * ONE slot for everything that demands a human before the queues: the stuck
 * items, on-call coverage gaps (next 60 days), and late POs — each a single
 * row with a kind chip and its real action. The whole band is ABSENT when
 * quiet; a rose border means something is hard-broken (conflict, no-heat,
 * approved time off on an on-call day), amber means "needs a decision."
 */
export function AlertBand({ selectTab }: { selectTab: (id: string) => void }) {
  const stuck = useStuck();
  const from = isoDate(new Date());
  const gaps = useOncallGaps(from, addDaysISO(from, 60)).data ?? [];
  const pos = useLatePos().data ?? [];

  const hardGaps = gaps.filter((g) => g.status === "approved");
  const softGaps = gaps.filter((g) => g.status === "requested");
  const empty = stuck.items.length === 0 && gaps.length === 0 && pos.length === 0;
  if (empty) return null;

  const rose = stuck.items.some((i) => i.tone === "rose") || hardGaps.length > 0;
  const vendors = [...new Set(pos.map((p) => p.vendor))];
  const vendorTxt = vendors.slice(0, 2).join(", ") + (vendors.length > 2 ? ` +${vendors.length - 2}` : "");

  const ctaCls =
    "inline-flex h-7 shrink-0 items-center rounded-md bg-slate-900 px-2.5 text-xs font-bold text-white transition-colors hover:bg-slate-700";
  const tagCls = (tone: "rose" | "amber" | "indigo") =>
    cn(
      "w-24 shrink-0 rounded-full px-2 py-0.5 text-center text-[11px] font-bold",
      tone === "rose" ? "bg-rose-100 text-rose-700" : tone === "amber" ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-700",
    );

  const cta = (it: StuckItem) => {
    if (it.cta.kind === "link") return <Link to={it.cta.to} className={ctaCls}>{it.cta.label}</Link>;
    if (it.cta.kind === "board") {
      const day = it.cta.day;
      return (
        <button type="button" onClick={() => showOnBoard(day)} title="Jump your board window to this day — the move tools live there" className={ctaCls}>
          {it.cta.label}
        </button>
      );
    }
    const id = it.cta.id;
    return (
      <button type="button" onClick={() => selectTab(id)} className={ctaCls}>
        {it.cta.label}
      </button>
    );
  };

  return (
    <div
      className={cn(
        "divide-y rounded-2xl border shadow-sm",
        rose ? "divide-rose-100 border-rose-200/80 bg-rose-50/40" : "divide-amber-100 border-amber-200/80 bg-amber-50/40",
      )}
    >
      {stuck.items.map((it) => (
        <div key={it.key} className="flex items-center gap-3 px-4 py-2.5">
          <span className={tagCls(it.tone)}>{it.tag}</span>
          <p className="min-w-0 flex-1 truncate text-[13.5px] text-slate-700">
            <span className="font-extrabold text-slate-900">{it.who}</span> — {it.what}
          </p>
          {it.meta && <span className="shrink-0 text-[11.5px] text-slate-400">{it.meta}</span>}
          {cta(it)}
        </div>
      ))}
      {gaps.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5">
          <span className={tagCls(hardGaps.length > 0 ? "rose" : "amber")}>Coverage</span>
          <p className="min-w-0 flex-1 truncate text-[13.5px] text-slate-700">
            <AlertTriangle className={cn("mr-1.5 inline h-3.5 w-3.5 align-[-2px]", hardGaps.length ? "text-rose-500" : "text-amber-500")} />
            {hardGaps.length > 0
              ? <><span className="font-extrabold text-slate-900">On-call coverage gap</span> — {hardGaps.length === 1 ? "1 day needs" : `${hardGaps.length} days need`} a swap{softGaps.length > 0 ? ` · ${softGaps.length} more at risk` : ""}</>
              : <><span className="font-extrabold text-slate-900">On-call at risk</span> — {softGaps.length === 1 ? "a time-off request is" : `${softGaps.length} time-off requests are`} waiting on an on-call day</>}
          </p>
          <Link to="/team?pane=oncall" className={ctaCls}>Open the calendar</Link>
        </div>
      )}
      {pos.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5">
          <span className={tagCls("indigo")}>Auto</span>
          <p className="min-w-0 flex-1 truncate text-[13.5px] text-slate-700">
            {pos.length === 1
              ? <><span className="font-extrabold text-slate-900">PO {pos[0].po_number ?? ""}</span> is past its expected date — {pos[0].vendor}</>
              : <><span className="font-extrabold text-slate-900">{pos.length} POs</span> past their expected date — {vendorTxt}</>}
          </p>
          <Link to="/purchase-orders" className={ctaCls}>Open POs</Link>
        </div>
      )}
      {stuck.extra > 0 && (
        <button
          type="button"
          onClick={() => selectTab("board")}
          className="block w-full px-4 py-1.5 text-left text-[12px] font-semibold text-slate-400 transition-colors hover:text-slate-600"
        >
          + {stuck.extra} more in the queues below
        </button>
      )}
    </div>
  );
}

/* ─────────────────── PEGASUS DID TODAY ─────────────────── */

interface AutoToday {
  reminders: number;
  outbox: number;
  outboxHeld: number;
  portal: number;
  trucks: number;
  shelf: number;
}

export function useAutoToday() {
  return useQuery({
    queryKey: ["auto_today", chicagoToday()],
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<AutoToday> => {
      const t0 = chicagoDayStartISO();
      const [rem, ob, pr, sc] = await Promise.all([
        supabase.from("reminder_sent").select("id", { count: "exact", head: true }).gte("sent_at", t0),
        supabase.from("pegasus_outbox").select("status").gte("created_at", t0).limit(500),
        supabase.from("portal_request").select("id", { count: "exact", head: true }).gte("created_at", t0),
        supabase.from("pegasus_stock_count").select("owner_empno").gte("created_at", t0).limit(1000),
      ]);
      const obRows = (ob.data ?? []) as { status: string }[];
      const scRows = (sc.data ?? []) as { owner_empno: string | null }[];
      return {
        reminders: rem.count ?? 0,
        outbox: obRows.filter((r) => r.status !== "cancelled").length,
        outboxHeld: obRows.filter((r) => r.status === "held").length,
        portal: pr.count ?? 0,
        trucks: new Set(scRows.map((r) => r.owner_empno).filter(Boolean)).size,
        shelf: scRows.filter((r) => !r.owner_empno).length,
      };
    },
  });
}

/** The footer line's "ran itself" fragments — empty array on a zero day. */
export function autoTodayBits(data: AutoToday | undefined): string[] {
  if (!data) return [];
  const bits: string[] = [];
  if (data.reminders > 0) bits.push(`${data.reminders} reminder${data.reminders === 1 ? "" : "s"} out`);
  if (data.outbox > 0)
    bits.push(
      data.outboxHeld === data.outbox
        ? `${data.outbox} message${data.outbox === 1 ? "" : "s"} staged (sending off)`
        : `${data.outbox} message${data.outbox === 1 ? "" : "s"} queued`,
    );
  if (data.portal > 0) bits.push(`${data.portal} self-serve request${data.portal === 1 ? "" : "s"}`);
  if (data.trucks > 0) bits.push(`${data.trucks} truck${data.trucks === 1 ? "" : "s"} counted`);
  if (data.shelf > 0) bits.push(`${data.shelf} shelf count${data.shelf === 1 ? "" : "s"}`);
  return bits;
}
